import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAppStore } from "@/store/app-store";

// Mirrors backend services/propertyAccessService.ts.
export const PERMISSION_OPTIONS = [
  { key: "BOOKINGS_VIEW", label: "View bookings", hint: "Bookings, dashboard, inventory and rooms" },
  { key: "BOOKINGS_MANAGE", label: "Create & confirm bookings", hint: "New bookings, confirm / reject requests, guest count" },
  { key: "CHECK_IN_OUT", label: "Check-in / check-out", hint: "Including early / late charges and housekeeping" },
  { key: "STAY_CHANGE", label: "Change dates & rooms", hint: "Dates, extend, change / exchange / add / remove room, rate plan, room blocks" },
  { key: "PAYMENTS", label: "Take payments", hint: "Record payments" },
  { key: "REFUNDS", label: "Give refunds", hint: "Record refunds" },
  { key: "DISCOUNTS", label: "Give discounts", hint: "Add / reverse discounts" },
  { key: "CHARGES_SERVICES", label: "Charges & services", hint: "Add / reverse extra charges and services" },
  { key: "CANCEL", label: "Cancel / no-show", hint: "Cancel bookings, mark no-show" },
  { key: "REPORTS", label: "Reports", hint: "Reports and collections (cash hisaab)" },
  { key: "SETTLEMENTS", label: "Settlements", hint: "See settlements" },
  { key: "PROPERTY_SETTINGS", label: "Property settings", hint: "Property details, rooms, room types, rate plans, services" },
  { key: "GUEST_IDS", label: "Guest IDs & police register", hint: "See guests' ID photos, download the police register" },
] as const;
export type Permission = (typeof PERMISSION_OPTIONS)[number]["key"];

export const PRESET_LABELS: Record<string, string> = { FULL_OWNER: "Full owner", MANAGER: "Manager", FRONT_DESK: "Front desk", CUSTOM: "Custom" };

export type DiscountLimit = { percent: number | null; amount: number | null } | null;
type AccessResponse = { propertyId: string; isAdmin: boolean; isMainOwner: boolean; canManageMembers: boolean; permissions: Permission[]; discountLimit: DiscountLimit };

// What the signed-in user may do at the active property. While loading nothing is allowed, so buttons never
// flash for someone who can't use them; the server checks every action anyway.
export function usePropertyAccess() {
  const propertyId = useAppStore((state) => state.activeProperty?.id);
  const role = useAppStore((state) => state.user?.role);
  const query = useQuery({
    queryKey: ["property-access", propertyId],
    queryFn: () => api<AccessResponse>(`/members/access?propertyId=${propertyId}`),
    enabled: Boolean(propertyId),
    staleTime: 60_000,
  });
  const permissions = new Set<Permission>(role === "ADMIN" ? PERMISSION_OPTIONS.map((option) => option.key) : query.data?.permissions ?? []);
  return {
    loading: query.isLoading,
    isMainOwner: Boolean(query.data?.isMainOwner),
    // Main owner / admin: no limit and they decide on others' requests.
    discountLimit: role === "ADMIN" ? null : query.data?.discountLimit ?? null,
    canManageMembers: role === "ADMIN" || Boolean(query.data?.canManageMembers),
    can: (permission: Permission) => permissions.has(permission),
  };
}
